import type {
  CreateRepairServiceTypeRequest,
  RepairServiceType,
  UpdateRepairServiceTypeRequest,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

export async function fetchRepairServiceTypes(
  signal?: AbortSignal,
  options?: { includeInactive?: boolean },
): Promise<RepairServiceType[]> {
  const query = options?.includeInactive ? '?includeInactive=true' : '';
  const response = await fetch(`${API_BASE_URL}/repair-service-types${query}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch repair service types: ${response.status} ${response.statusText}`,
    );
  }
  const data = await response.json();
  return data.items ?? [];
}

export async function createRepairServiceType(
  payload: CreateRepairServiceTypeRequest,
  signal?: AbortSignal,
): Promise<RepairServiceType> {
  const response = await fetch(`${API_BASE_URL}/repair-service-types`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({
      error: `Failed to create repair service type: ${response.status}`,
    }));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function updateRepairServiceType(
  repairServiceTypeId: string,
  payload: UpdateRepairServiceTypeRequest,
  signal?: AbortSignal,
): Promise<RepairServiceType> {
  const response = await fetch(
    `${API_BASE_URL}/repair-service-types/${repairServiceTypeId}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal,
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({
      error: `Failed to update repair service type: ${response.status}`,
    }));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function deleteRepairServiceType(
  repairServiceTypeId: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/repair-service-types/${repairServiceTypeId}`,
    { method: 'DELETE', signal },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({
      error: `Failed to delete repair service type: ${response.status}`,
    }));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
}
